import React from 'react'

import { useTranslation } from 'react-i18next'
import { ContentStateProperty, InputType } from '../../../../@types/types'
import { InputProps } from '../../../../@types/props-types'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { UnitRight } from '../textInput/TextInput'
import { NumericInput } from 'is24-corecss'
import RemainingCharacters from '../remainingCharacters/RemainingCharacters'

type Locale = 'de-DE' | 'en-US'
type Precision = 0 | 1 | 2 | 3

export interface NumberInputProps extends InputProps {
  type?: InputType
  className?: string
  style?: React.CSSProperties
  placeholder?: string
  label?: string | React.ReactNode
  ariaLabel?: string
  value?: number
  maxLength?: number
  showMaxLengthLabel?: boolean
  errors: string[]
  disabled?: boolean
  affixRight?: 'none' | 'unit'
  unitRight?: UnitRight
  affixRightStyle?: React.CSSProperties
  hideErrorMessageWithValidation?: boolean
  showRedBoarder?: boolean
  doOnFieldChange: (field: ContentStateProperty, value: number | undefined) => void
  onBlur?: () => void
  onBlurTracking?: (field: ContentStateProperty, value: string, isUnchanged: boolean) => void
  onFocus?: () => void
  locale?: Locale
  minPrecision?: Precision
  maxPrecision?: Precision
  hideMandatorySign?: boolean
  dataHeyimmoContext?: string
}

const DEFAULT_LOCALE = 'de-DE'
const DEFAULT_GROUP_SEPARATOR = '.'
const DEFAULT_DECIMAL_SEPARATOR = ','

export const findSeparator = (parts: Intl.NumberFormatPart[], type: string, defaultValue: string) =>
  parts.find((part: Intl.NumberFormatPart) => part.type === type)?.value ?? defaultValue

const parseNumber = (value: string, locale: string, precision: Precision) => {
  const numberFormat = new Intl.NumberFormat(locale, { style: 'decimal' })
  const parts = numberFormat.formatToParts(1234567.89)
  const groupSeparator = findSeparator(parts, 'group', DEFAULT_GROUP_SEPARATOR)
  const decimalSeparator = findSeparator(parts, 'decimal', DEFAULT_DECIMAL_SEPARATOR)

  const cleanValue = value.split(groupSeparator).join('').split(decimalSeparator).join('.')
  return parseFloat(parseFloat(cleanValue).toFixed(precision))
}

/**
 * NumberInput wrapper around Cosma's NumericInput component.
 *
 * MIGRATION NOTES:
 * - Wrapped Cosma's NumericInput (from is24-corecss) to maintain backward compatibility
 * - Missing features vs. original implementation:
 *   1. autocomplete="nope" - Not supported by Cosma's NumericInput
 *   2. onBlurTracking - Temporarily disabled for MVP (see TODO in handleBlur)
 *   3. minPrecision - Cosma only supports maxPrecision (decimalScale)
 *      - Values won't display trailing zeros unless user types them
 *
 * All other functionality is preserved through the wrapper.
 */
export const NumberInput: React.FunctionComponent<NumberInputProps> = ({
  locale = DEFAULT_LOCALE,
  minPrecision: _minPrecision = 0,
  maxPrecision = 0,
  hideMandatorySign = false,
  ...props
}: NumberInputProps) => {
  const { t } = useTranslation()
  const { setTouched, formState } = useFormState()

  // Pass the raw number value to Cosma NumericInput, let it handle formatting
  const rawValue = props.value

  // Handle onValueChange from react-number-format (used by Cosma NumericInput)
  const handleValueChange = (values: { floatValue: number | undefined }) => {
    const { floatValue } = values
    try {
      props.doOnFieldChange(props.propertyName, floatValue)
    } catch (error) {
      console.error('Error in NumberInput handleValueChange:', error)
    }
  }

  // Handle blur with form state tracking
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    try {
      props.onBlur?.()
      setTouched(props.propertyName)

      // Handle case where input is cleared but onValueChange wasn't called
      const currentValue = event.target.value
      if (currentValue === '' && props.value !== undefined) {
        props.doOnFieldChange(props.propertyName, undefined)
      }

      // TODO: Implement onBlurTracking when needed for analytics
      // Currently disabled for MVP migration
      if (props.onBlurTracking && currentValue !== '') {
        const isUnchanged = currentValue === String(props.value || '')
        props.onBlurTracking(props.propertyName, currentValue, isUnchanged)
      }
    } catch (error) {
      console.error('Error in NumberInput handleBlur:', error)
    }
  }

  // Compute validation state
  const showErrors = React.useMemo(() => {
    return props.errors.length > 0 && (formState.submitCount > 0 || formState.touched[props.propertyName])
  }, [props.errors.length, formState.submitCount, formState.touched, props.propertyName])

  // Map unitRight to Cosma's unit format
  const mapUnitRight = (unitRight?: UnitRight): UnitRight | undefined => {
    if (!unitRight || unitRight === 'none') return undefined
    return unitRight
  }

  return (
    <div id={`${props.id}-input-text-container`} className={`input-text-container ${props.className || ''}`}>
      <NumericInput
        id={props.id}
        label={props.label ?? ''}
        placeholder={props.placeholder ?? ''}
        value={rawValue}
        thousandSeparator={locale === 'de-DE' ? '.' : ','}
        decimalSeparator={locale === 'de-DE' ? ',' : '.'}
        decimalScale={maxPrecision > 0 ? maxPrecision : undefined}
        allowLeadingZeros={true}
        allowNegative={false}
        isAllowed={(values) => {
          // Allow all values during typing, let validation handle restrictions
          // Only prevent extremely large numbers that could cause system issues
          // The validation rules will handle business logic restrictions
          if (values.floatValue && values.floatValue > 999999999999999) {
            return false
          }
          return true
        }}
        onValueChange={handleValueChange}
        onBlur={handleBlur}
        disabled={props.disabled}
        maxLength={props.maxLength}
        valid={!showErrors && !props.showRedBoarder}
        success={props.value !== undefined && !showErrors && !props.showRedBoarder}
        errorMessage={
          showErrors && !props.hideErrorMessageWithValidation ? t(`validation.${props.errors[0]}`) : undefined
        }
        unitRight={mapUnitRight(props.unitRight)}
        affixRight={props.affixRight === 'unit' ? 'unit' : 'none'}
        mandatory={!hideMandatorySign}
        style={props.style}
        {...(props.ariaLabel && { 'aria-label': props.ariaLabel })}
        {...(props.onFocus && { onFocus: props.onFocus })}
      />
      {props.dataHeyimmoContext && (
        <span data-heyimmo-context={props.dataHeyimmoContext} style={{ display: 'none' }} aria-hidden='true'>
          {rawValue ?? ''}
        </span>
      )}
      {props.showMaxLengthLabel && (
        <RemainingCharacters
          maxLength={props.maxLength}
          valueLength={props.value ? String(props.value).length : undefined}
        />
      )}
    </div>
  )
}

export { parseNumber }
